import { useEffect, useCallback, useState, useMemo } from 'react';
import { useHistory, useParams, useLocation, matchPath } from 'react-router-dom';
import Routes from 'routes';
import AuthManager from 'auth';
import { AppChoicePage } from './app-choice-page';

interface QuickLinkParams {
    type: string;
}

interface ProfileMatchParams {
    id: string;
}

const QuickLink = () => {
    const history = useHistory();
    const { type } = useParams<QuickLinkParams>();
    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const [showAppChoice, setShowAppChoice] = useState(false);

    const auth = AuthManager.getInstance();
    const isLoggedIn = !!auth.getToken();

    // Check if user is on mobile iOS or Android
    useEffect(() => {
        const userAgent = navigator.userAgent.toLowerCase();
        const isIOS = /iphone|ipad|ipod/.test(userAgent);
        const isAndroid = /android/.test(userAgent);
        
        if ((isIOS || isAndroid) && !queryParams.get('continue_in_browser')) {
            setShowAppChoice(true);
        }
    }, [queryParams]);

    const handleProfileLink = useCallback(() => {
        const match = matchPath<ProfileMatchParams>(location.pathname, {
            path: '/l/profile/:id',
            exact: true
        });
        
        if (match?.params?.id) {
            const profilePath = Routes.holder_profile.replace(':id', match.params.id);
            history.replace(profilePath);
            return true;
        }
        return false;
    }, [location.pathname, history]);

    const handleNavigation = useCallback(() => {
        if (type === 'profile' && handleProfileLink()) {
            return;
        }
        history.replace(Routes.holder_list);
    }, [history, type, handleProfileLink]);

    const handleContinueInBrowser = useCallback(() => {
        setShowAppChoice(false);
        // Add continue_in_browser param to prevent showing app choice again
        const newSearch = new URLSearchParams(location.search);
        newSearch.set('continue_in_browser', 'true');
        history.replace({ search: newSearch.toString() });
    }, [history, location.search]);

    useEffect(() => {
        if (showAppChoice) return;

        if (!isLoggedIn) {
            const returnPath = `${location.pathname}${location.search}`;
            history.replace(`${Routes.login}?return_to=${encodeURIComponent(returnPath)}`);
            return;
        }

        handleNavigation();
    }, [history, location.pathname, location.search, isLoggedIn, handleNavigation, showAppChoice]);

    if (showAppChoice) {
        return <AppChoicePage onContinueInBrowser={handleContinueInBrowser} />;
    }

    return null;
};

export default QuickLink; 